import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  AdminCreateUserPayload,
  AdminCreateUserResponse,
  GamerNameType,
  UserAlert,
  UserDetail,
  TransactionResponse,
  AlertType,
  UserAlertInfo,
  ListAlertResponse,
} from '../models/user-alert';

@Injectable({
  providedIn: 'root',
})
export class UserAlertService {
  private baseUrl = `${environment.apiUrl}/admin/accounts/`;
  private notesUrl = `${environment.apiUrl}/admin/account_notes/`;

  constructor(private http: HttpClient) { }

  listUsers(
    search?: string,
    filters?: { has_alert?: boolean; is_limited?: boolean; page?: number }
  ): Observable<{ count: number; results: UserAlert[] }> {
    let params = new HttpParams();
    if (search) {
      params = params.append('search', search);
    }
    if (filters?.has_alert) {
      params = params.append('has_alert', 'true');
    }
    if (filters?.is_limited) {
      params = params.append('is_limited', 'true');
    }
    if (filters?.page) {
      params = params.append('page', filters.page.toString());
    }

    return this.http.get<{ count: number; results: UserAlert[] }>(this.baseUrl, { params });
  }

  retrieveUser(id: number): Observable<UserDetail> {
    const url = `${this.baseUrl}${id}/`;
    return this.http.get<UserDetail>(url);
  }

  updateInfluencerStatus(id: number, is_influencer: boolean): Observable<UserDetail> {
    const url = `${this.baseUrl}${id}/`;
    return this.http.patch<UserDetail>(url, { is_influencer });
  }

  updateUserTrusted(
    id: number,
    is_trusted: boolean,
    reason?: string | null
  ): Observable<UserDetail> {
    const body: { is_active: boolean; reason?: string } = { is_active: is_trusted };
    if (!is_trusted && reason?.trim()) {
      body.reason = reason.trim();
    }
    return this.http
      .post<void>(`${this.baseUrl}${id}/trusted_override/`, body)
      .pipe(switchMap(() => this.retrieveUser(id)));
  }

  unlinkGamerName(id: number, type: GamerNameType): Observable<null> {
    const url = `${this.baseUrl}${id}/remove_gamername/`;
    return this.http.post<null>(url, { type });
  }

  updateUserEmail(id: number, newEmail: string): Observable<UserDetail> {
    const url = `${this.baseUrl}${id}/edit_email/`;
    return this.http.patch<UserDetail>(url, { email: newEmail });
  }

  createUser(payload: AdminCreateUserPayload): Observable<AdminCreateUserResponse> {
    const url = `${this.baseUrl}create/`;
    return this.http.post<AdminCreateUserResponse>(url, payload);
  }

  createAccountNote(userId: number, description: string) {
    return this.http.post(this.notesUrl, {
      user_id: userId,
      description: description
    });
  }

  updateAccountNote(noteId: number, description: string) {
    return this.http.patch(`${this.notesUrl}${noteId}/`, { description });
  }

  deleteAccountNote(noteId: number) {
    return this.http.delete(`${this.notesUrl}${noteId}/`);
  }

  forcePasswordReset(email: string): Observable<null> {
    const url = `${environment.apiUrl}/accounts/reset_password/`;
    return this.http.patch<null>(url, { email });
  }

  getUserTransactions(userId: number, page?: number, types?: string[]): Observable<TransactionResponse> {
    const url = `${environment.apiUrl}/accounts/${userId}/transactions/`;
    let params = new HttpParams();
    params = params.append('page_size', '20');
    if (page) {
      params = params.append('page', page.toString());
    }
    if (types && types.length > 0) {
      params = params.append('type', types.join(','));
    }
    return this.http.get<TransactionResponse>(url, { params });
  }

  deactivateAlert(accountId: number, alertId: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}${accountId}/alerts/${alertId}/`);
  }

  createAlert(accountId: number, alertData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}${accountId}/alerts/`, alertData);
  }

  listAlerts(accountId: number): Observable<ListAlertResponse> {
    return this.http.get<ListAlertResponse>(`${this.baseUrl}${accountId}/alerts/`)
  }

  deactivateAlerts(accountId: number): Observable<UserAlertInfo[]> {
    return this.http.delete<UserAlertInfo[]>(`${this.baseUrl}${accountId}/alerts/`)
  }

  getAlertTypes(): Observable<AlertType[]> {
    return this.http.get<AlertType[]>(`${this.baseUrl}alert-types/`)
  }
}
